import { FormProvider, useFormContext, useWatch } from 'react-hook-form'
import { Button, Checkbox, Form, formatFileSize, Heading, TextArea, TextField } from '@boltenergy-be/design-system'
import { ComplaintFlowInputs, ComplaintFlowProps } from '../types'
import FileUpload from 'components/form-fields/file-upload/FileUpload'
import { useTranslation } from 'react-i18next'
import {
  COMPLAINT_FILE_UPLOAD_AMOUNT_LIMIT,
  COMPLAINT_FILE_UPLOAD_SIZE_LIMIT,
  MAX_COMPLAINT_CHARACTERS
} from 'pages/app/support/complaint-form/constants.ts'
import { useGetLanguage } from 'hooks/use-get-language'

const Complaint = ({ setCurrentStep }: ComplaintFlowProps) => {
  // i18n
  const { t } = useTranslation('support', { keyPrefix: 'complaintForm.complaint' })
  const { t: tX } = useTranslation(['common', 'validation'])
  const language = useGetLanguage()

  // React Hook Form
  const hookForm = useFormContext<ComplaintFlowInputs>()
  const { handleSubmit, register, formState, control } = hookForm
  const watchComplaint = useWatch({ control, name: 'description' })

  /**
   * Submit handler for the contact step of the complaint flow.
   */
  const submit = () => {
    setCurrentStep('overview')
  }

  return (
    <section>
      <header className="flex-col gap-200 mb-400">
        <Heading as="h2" variant="h4">
          {t(($) => $.title)}
        </Heading>
        <p>{t(($) => $.description)}</p>
      </header>

      <Form onSubmit={handleSubmit(submit)}>
        <TextField
          {...register('subject', {
            required: tX(($) => $.required, {
              ns: 'validation'
            }),
            maxLength: {
              value: 100,
              message: tX(($) => $.invalid.length, {
                length: 100,
                ns: 'validation'
              })
            }
          })}
          grid="full"
          label={t(($) => $.subject.label)}
          description={t(($) => $.subject.description)}
          required
          error={formState.errors?.subject?.message}
        />

        <TextArea
          {...register('description', {
            required: tX(($) => $.required, {
              ns: 'validation'
            }),
            maxLength: {
              value: MAX_COMPLAINT_CHARACTERS,
              message: tX(($) => $.invalid.length, {
                length: MAX_COMPLAINT_CHARACTERS,
                ns: 'validation'
              })
            }
          })}
          grid="full"
          label={t(($) => $.reason.label)}
          description={t(($) => $.reason.description)}
          required
          error={formState.errors?.description?.message}
          maxLength={MAX_COMPLAINT_CHARACTERS}
          message={
            watchComplaint?.length === MAX_COMPLAINT_CHARACTERS ? { type: 'informative', text: t(($) => $.reason.limitReached) } : undefined
          }
        />

        <FormProvider {...hookForm}>
          <FileUpload
            identifier="attachments"
            multiple
            maxAmountFiles={COMPLAINT_FILE_UPLOAD_AMOUNT_LIMIT}
            limit={COMPLAINT_FILE_UPLOAD_SIZE_LIMIT}
            label={t(($) => $.attachment.label)}
            description={t(($) => $.attachment.description, {
              amountLimit: 15,
              sizeLimit: formatFileSize(COMPLAINT_FILE_UPLOAD_SIZE_LIMIT, language)
            })}
          />
        </FormProvider>

        <Checkbox
          {...register('phoneContactAllowed')}
          id="phoneContactAllowed"
          label={t(($) => $.phoneContactAllowed)}
          className="grid-col-full"
        />

        <Form.Footer className="flex-row">
          <Button type="submit" className="ml-auto" trailingIcon="arrowRight">
            {tX(($) => $.next)}
          </Button>
        </Form.Footer>
      </Form>
    </section>
  )
}

export default Complaint
